/**
 * CSV parsing for the auto-enroll upload. The cell splitting and header matching are `./csv`'s.
 *
 * The API is the authority on what's valid (it re-validates and de-dupes every
 * row). This exists so an admin sees the problems *before* sending 200 rows, and
 * so sub-org names typed by a human become the ids the mutation wants.
 */

import { findColumn, normalizeHeader, parseCsv } from "./csv";

/** One data row, mapped onto what `bulkAutoEnroll` accepts. */
export interface AutoEnrollCsvRow {
    /** 1-based line in the file, for pointing at the offending row. */
    line: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
    subOrganizationIds: string[];
    /** The names as typed, so the preview can show what was matched. */
    subOrganizationNames: string[];
    brand: string | null;
    /** Set when this row can't be sent; it is excluded from the request. */
    error?: string;
}

export interface AutoEnrollCsvResult {
    rows: AutoEnrollCsvRow[];
    /** A problem with the file as a whole (no header, no data) — nothing is sendable. */
    fatal?: string;
}

/** Header spellings we accept per column — people do not type the same thing twice. */
const HEADERS = {
    email: [
        "email",
        "emailaddress",
        "email address",
        "e-mail",
    ],
    firstName: [
        "firstname",
        "first name",
        "first",
        "given name",
    ],
    lastName: [
        "lastname",
        "last name",
        "last",
        "surname",
        "family name",
    ],
    /*
     * Wide on purpose. This column is the one that arrives with a partner's own header on it —
     * `MOBILE_PHONE` out of an SIS export is what prompted adding it — and a spelling that is not
     * listed is not an error the admin sees. The column is simply ignored and every number in the
     * file is dropped without a word, which is exactly how 400 of them went missing.
     */
    phoneNumber: [
        "phone",
        "phonenumber",
        "phone number",
        "mobile",
        "mobilephone",
        "mobile phone",
        "mobile number",
        "cell",
        "cellphone",
        "cell phone",
        "telephone",
    ],
    subOrganization: [
        "sub-org",
        "sub org",
        "suborg",
        "sub-organization",
        "sub organization",
        "suborganization",
        "chapter",
    ],
    brand: [
        "brand",
        "app identity",
        "identity",
    ],
} as const;

/** Brand spellings, including the product names an admin would actually write. */
const BRANDS: Record<string, string> = {
    release_u: "RELEASE_U",
    releaseu: "RELEASE_U",
    "release u": "RELEASE_U",
    release: "RELEASE_U",
    manual: "MANUAL",
    grace: "GRACE",
};

/** Deliberately loose — the server's validator is the authority. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface SubOrgOption {
    id: string;
    name: string;
}

/**
 * Maps CSV text onto enrollable rows.
 *
 * `subOrgs` are the target org's descendants: a human types "North Campus", the
 * mutation needs its id, and a name that matches nothing has to be caught here —
 * the server rejects the *whole* request for one bad sub-org id, so letting a
 * typo through would fail the entire upload.
 */
export const parseAutoEnrollCsv = (text: string, subOrgs: SubOrgOption[]): AutoEnrollCsvResult => {
    const table = parseCsv(text);
    if (table.length === 0) return { rows: [], fatal: "That file is empty." };

    const headers = table[0] ?? [];
    const emailColumn = findColumn(headers, HEADERS.email);
    if (emailColumn === -1) {
        return {
            rows: [],
            fatal: "No email column found. The first row must be a header — download the template to see the expected columns.",
        };
    }

    const firstNameColumn = findColumn(headers, HEADERS.firstName);
    const lastNameColumn = findColumn(headers, HEADERS.lastName);
    const phoneColumn = findColumn(headers, HEADERS.phoneNumber);
    const subOrgColumn = findColumn(headers, HEADERS.subOrganization);
    const brandColumn = findColumn(headers, HEADERS.brand);

    const byName = new Map(
        subOrgs.map((org) => [
            org.name.trim().toLowerCase(),
            org.id,
        ]),
    );
    const byId = new Map(
        subOrgs.map((org) => [
            org.id,
            org.name,
        ]),
    );

    const cell = (cells: string[], index: number): string => (index === -1 ? "" : (cells[index] ?? "").trim());

    const firstSeenAt = new Map<string, number>();

    const rows = table.slice(1).map((cells, index) => {
        const line = index + 2; // +1 for the header, +1 for 1-based lines.
        const email = cell(cells, emailColumn).toLowerCase();
        const row: AutoEnrollCsvRow = {
            line,
            email,
            firstName: cell(cells, firstNameColumn) || null,
            lastName: cell(cells, lastNameColumn) || null,
            phoneNumber: cell(cells, phoneColumn) || null,
            subOrganizationIds: [],
            subOrganizationNames: [],
            brand: null,
        };

        if (!email) return { ...row, error: "Missing email" };
        if (!LOOKS_LIKE_EMAIL.test(email)) return { ...row, error: "Not a valid email address" };

        // Flagged here rather than left for the server: the verdict is identical
        // either way, and catching it now keeps every sent row matched 1:1 with the
        // result that comes back, so the table can report outcomes per line.
        const firstSeen = firstSeenAt.get(email);
        if (firstSeen !== undefined) return { ...row, error: `Duplicate of line ${firstSeen}` };
        firstSeenAt.set(email, line);

        // Semicolons, not commas — a comma is the field separator.
        const rawSubOrgs = cell(cells, subOrgColumn)
            .split(";")
            .map((value) => value.trim())
            .filter(Boolean);
        for (const value of rawSubOrgs) {
            const id = byName.get(value.toLowerCase()) ?? (byId.has(value) ? value : undefined);
            if (!id) {
                return { ...row, error: `No sub-organization named "${value}"` };
            }
            row.subOrganizationIds.push(id);
            row.subOrganizationNames.push(byId.get(id) ?? value);
        }

        const rawBrand = cell(cells, brandColumn);
        if (rawBrand) {
            const brand = BRANDS[normalizeHeader(rawBrand)];
            if (!brand) return { ...row, error: `Unknown brand "${rawBrand}"` };
            row.brand = brand;
        }

        return row;
    });

    if (rows.length === 0) return { rows: [], fatal: "That file has a header but no rows." };
    return { rows };
};

/**
 * Maps a parsed row onto what `bulkAutoEnroll` accepts.
 *
 * A brand named in the file wins over the page-level default, so one CSV can mix
 * brands while the common case needs no brand column at all. `null` means "say
 * nothing" — the server then applies its own default on activation.
 */
export const toEnrollRow = (row: AutoEnrollCsvRow, fallbackBrand: string) => ({
    email: row.email,
    firstName: row.firstName,
    lastName: row.lastName,
    phoneNumber: row.phoneNumber,
    subOrganizationIds: row.subOrganizationIds,
    brand: row.brand ?? (fallbackBrand || null),
});

/** The starter file behind "Download template" — headers plus one example row. */
export const AUTO_ENROLL_TEMPLATE = [
    "email,first name,last name,phone number,sub-org,brand",
    "someone@example.org,Jordan,Lee,+1 555 123 4567,,RELEASE_U",
].join("\n");
